import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertCircle,
  Loader2,
  MessageSquareQuote,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Search,
  Star,
  Trash2,
  X,
} from 'lucide-react';
import { Pagination } from '../../components/ui/Pagination';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { WebsiteTestimonialModal } from './WebsiteTestimonialModal';
import {
  useBulkDeleteTestimonials,
  useDeleteTestimonial,
  useUpdateTestimonial,
  useWebsiteTestimonials,
} from '../../services/httpServices/queries';
import type { WebsiteTestimonial } from '../../services/httpServices/websiteService';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

function Stars({ rating }: { rating: number }) {
  return (
    <span className="inline-flex items-center gap-0.5 text-warning">
      {Array.from({ length: 5 }).map((_, index) => (
        <Star
          key={index}
          size={13}
          fill={index < rating ? 'currentColor' : 'none'}
          className={index < rating ? 'text-warning' : 'text-text-muted/30'}
        />
      ))}
    </span>
  );
}

/**
 * Manages the patient testimonials shown on the public `/testimonials` page.
 * Mirrors the gallery screen so admins get the same search, filter, bulk
 * delete and inline publish/hide workflow.
 */
export function WebsiteTestimonials() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<WebsiteTestimonial | null>(null);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WebsiteTestimonial | null>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, isFetching, error } = useWebsiteTestimonials({
    page,
    limit: 20,
    search: debouncedSearch,
    status,
  });

  const items = useMemo(() => data?.data ?? [], [data]);
  const meta = data?.meta;

  const updateMutation = useUpdateTestimonial();
  const deleteMutation = useDeleteTestimonial();
  const bulkDeleteMutation = useBulkDeleteTestimonials();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setPage(1);
    setSelected([]);
  }, [status]);

  useEffect(() => {
    const visible = new Set(items.map((item) => item.id));
    setSelected((prev) => {
      const next = prev.filter((id) => visible.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [items]);

  const allSelected = items.length > 0 && items.every((item) => selected.includes(item.id));
  const someSelected = items.some((item) => selected.includes(item.id));

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleAll = () => {
    setSelected(allSelected ? [] : items.map((item) => item.id));
  };

  const toggleOne = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]));

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (testimonial: WebsiteTestimonial) => {
    setEditing(testimonial);
    setModalOpen(true);
  };

  const handleToggleActive = async (testimonial: WebsiteTestimonial) => {
    try {
      await updateMutation.mutateAsync({
        id: testimonial.id,
        data: { isActive: !testimonial.isActive },
      });
    } catch {
      toast.error('Failed to update testimonial');
    }
  };

  const handleDeleteOne = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success('Testimonial deleted');
      setDeleteTarget(null);
    } catch {
      toast.error('Failed to delete testimonial');
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...selected];
    try {
      await bulkDeleteMutation.mutateAsync(ids);
      toast.success(`Deleted ${ids.length} testimonial${ids.length === 1 ? '' : 's'}`);
      setSelected([]);
      setBulkConfirmOpen(false);
    } catch {
      toast.error('Failed to delete selected testimonials');
    }
  };

  const hasFilters = !!debouncedSearch || !!status;
  const isInitialLoading = isLoading && items.length === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center">
            <MessageSquareQuote className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text">Testimonials</h1>
            <p className="text-sm text-text-muted">
              Manage the patient reviews shown on the public /testimonials page.
            </p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium flex items-center gap-2"
        >
          <Plus size={16} />
          Add testimonial
        </button>
      </div>

      {/* Filters */}
      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              placeholder="Search by name, role, heading or quote…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-danger"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50 sm:w-48"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Selection bar */}
      {selected.length > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-text">
            <span className="font-medium">{selected.length}</span> selected
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setBulkConfirmOpen(true)}
              disabled={bulkDeleteMutation.isPending}
              className="px-3 py-1.5 bg-danger text-white rounded-lg hover:bg-danger/90 transition-colors text-sm font-medium disabled:opacity-50 flex items-center gap-1.5"
            >
              {bulkDeleteMutation.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Trash2 size={14} />
              )}
              Delete
            </button>
            <button
              type="button"
              onClick={() => setSelected([])}
              disabled={bulkDeleteMutation.isPending}
              className="px-3 py-1.5 text-sm text-text-muted hover:text-text border border-border rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5"
            >
              <X size={14} />
              Clear selection
            </button>
          </div>
        </div>
      )}

      {isInitialLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : error && items.length === 0 ? (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load testimonials. Please try again.</span>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-surface rounded-xl border border-dashed border-border py-16 text-center">
          <MessageSquareQuote className="w-10 h-10 text-text-muted mx-auto mb-3" />
          <p className="text-text font-medium">
            {hasFilters
              ? 'No testimonials match your filters.'
              : 'No testimonials yet. Add your first review to get started.'}
          </p>
          {hasFilters ? (
            <button
              onClick={() => {
                setSearch('');
                setStatus('');
              }}
              className="mt-2 text-sm text-primary hover:underline"
            >
              Clear filters
            </button>
          ) : (
            <button
              onClick={openCreate}
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
            >
              <Plus size={16} />
              Add testimonial
            </button>
          )}
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          {isFetching && (
            <div className="flex items-center gap-2 px-4 py-2 border-b border-border text-xs text-text-muted">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Updating…
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 w-10 text-left">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={items.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">
                    Author
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">
                    Quote
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase">
                    Rating
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-text-muted uppercase">
                    Order
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">
                    Status
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {items.map((testimonial) => (
                  <tr key={testimonial.id} className="hover:bg-background/50 transition-colors">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.includes(testimonial.id)}
                        onChange={() => toggleOne(testimonial.id)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {testimonial.image ? (
                          <img
                            src={testimonial.image}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover border border-border flex-shrink-0"
                          />
                        ) : (
                          <span className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold flex-shrink-0">
                            {testimonial.name.charAt(0).toUpperCase()}
                          </span>
                        )}
                        <div className="min-w-0">
                          <button
                            onClick={() => openEdit(testimonial)}
                            className="text-sm font-medium text-text hover:text-primary text-left truncate block max-w-[200px]"
                          >
                            {testimonial.name}
                          </button>
                          <p className="text-xs text-text-muted truncate max-w-[200px]">
                            {testimonial.role || '—'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {testimonial.title && (
                        <p className="text-sm font-medium text-text truncate max-w-[280px]">
                          {testimonial.title}
                        </p>
                      )}
                      <p className="text-sm text-text-muted truncate max-w-[280px]">
                        {testimonial.quote}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Stars rating={testimonial.rating} />
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-text-muted">{testimonial.displayOrder}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                          testimonial.isActive
                            ? 'bg-success/10 text-success'
                            : 'bg-danger/10 text-danger'
                        }`}
                      >
                        {testimonial.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleToggleActive(testimonial)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title={testimonial.isActive ? 'Hide from website' : 'Show on website'}
                        >
                          {testimonial.isActive ? <PowerOff size={16} /> : <Power size={16} />}
                        </button>
                        <button
                          onClick={() => openEdit(testimonial)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                          title="Edit"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(testimonial)}
                          className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            page={page}
            totalPages={meta?.totalPages || 1}
            total={meta?.total || 0}
            limit={20}
            onPageChange={setPage}
            itemName="testimonials"
          />
        </div>
      )}

      <WebsiteTestimonialModal
        isOpen={modalOpen}
        testimonial={editing}
        onClose={() => setModalOpen(false)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete testimonial?"
        description={
          <>
            This will remove the testimonial from{' '}
            <strong className="text-text">{deleteTarget?.name}</strong> and hide it from the public
            website. This cannot be undone.
          </>
        }
        confirmLabel="Delete testimonial"
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteOne}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selected.length} testimonial${selected.length === 1 ? '' : 's'}?`}
        description="This removes the selected testimonials from the public website. This cannot be undone."
        confirmLabel={`Delete ${selected.length} testimonial${selected.length === 1 ? '' : 's'}`}
        loading={bulkDeleteMutation.isPending}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkConfirmOpen(false)}
      />
    </div>
  );
}
